import React, { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { Input, Modal, ModalHeader, ModalBody } from 'reactstrap'
import moment from 'moment'
import 'react-datepicker/dist/react-datepicker.css'
import './DateRangePicker.css'

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, containerClassName }) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [tempStartDate, setTempStartDate] = useState(null)
    const [tempEndDate, setTempEndDate] = useState(null)

    // Initialize temp dates from props when props change
    useEffect(() => {
        if (startDate) {
            setTempStartDate(moment(startDate).toDate())
        } else {
            setTempStartDate(null)
        }
        if (endDate) {
            setTempEndDate(moment(endDate).toDate())
        } else {
            setTempEndDate(null)
        }
    }, [startDate, endDate])

    // Reset temp dates when modal opens
    useEffect(() => {
        if (isModalOpen) {
            if (startDate) {
                setTempStartDate(moment(startDate).toDate())
            } else {
                setTempStartDate(null)
            }
            if (endDate) {
                setTempEndDate(moment(endDate).toDate())
            } else {
                setTempEndDate(null)
            }
        }
    }, [isModalOpen, startDate, endDate])

    const minDate = moment().subtract(1, 'year').toDate() // Allow selecting dates up to 1 year ago
    const maxDate = moment().toDate() // Can't select future dates

    const formatDateDisplay = () => {
        if (!startDate || !endDate) return 'Select date range'
        const start = moment(startDate).format('MMM DD, YYYY')
        const end = moment(endDate).format('MMM DD, YYYY')
        return `${start} - ${end}`
    }

    const handleConfirm = () => {
        if (tempStartDate && tempEndDate) {
            // Ensure start date is before end date
            if (moment(tempStartDate).isAfter(moment(tempEndDate))) {
                // Swap if needed
                const temp = tempStartDate
                setTempStartDate(tempEndDate)
                setTempEndDate(temp)
                onStartDateChange(moment(tempEndDate).format('YYYY-MM-DD'))
                onEndDateChange(moment(temp).format('YYYY-MM-DD'))
            } else {
                onStartDateChange(moment(tempStartDate).format('YYYY-MM-DD'))
                onEndDateChange(moment(tempEndDate).format('YYYY-MM-DD'))
            }
            setIsModalOpen(false)
        }
    }

    const handleStartDateChange = date => {
        setTempStartDate(date)
        // If end date is before new start date, update end date
        if (tempEndDate && moment(date).isAfter(moment(tempEndDate))) {
            setTempEndDate(date)
        }
    }

    const handleEndDateChange = date => {
        setTempEndDate(date)
        // If start date is after new end date, update start date
        if (tempStartDate && moment(date).isBefore(moment(tempStartDate))) {
            setTempStartDate(date)
        }
    }

    return (
        <>
            <div className={`date-range-picker ${containerClassName || ''}`}>
                <div className="date-range-input-wrapper">
                    <i className="bx bx-calendar calendar-icon"></i>
                    <Input
                        type="text"
                        value={formatDateDisplay()}
                        onClick={() => setIsModalOpen(true)}
                        readOnly
                        className="date-range-input"
                        placeholder="Select date range"
                    />
                </div>
            </div>

            {/* Date Range Modal */}
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} className="date-range-modal" centered>
                <ModalHeader toggle={() => setIsModalOpen(false)}>Select Date Range</ModalHeader>
                <ModalBody>
                    <div className="date-range-picker-container">
                        <div className="date-picker-section">
                            <h5 className="date-picker-label">Start Date</h5>
                            <DatePicker
                                key={`start-${isModalOpen}-${tempStartDate?.getTime()}`}
                                selected={tempStartDate}
                                onChange={handleStartDateChange}
                                minDate={minDate}
                                maxDate={tempEndDate || maxDate}
                                dateFormat="MMMM d, yyyy"
                                calendarClassName="date-range-calendar date-range-calendar-start"
                                inline
                                locale="en-GB"
                                formatWeekDay={dayName => {
                                    const shortDay = dayName.substring(0, 3)
                                    return shortDay.charAt(0).toUpperCase() + shortDay.slice(1).toLowerCase()
                                }}
                            />
                        </div>
                        <div className="date-picker-section">
                            <h5 className="date-picker-label">End Date</h5>
                            <DatePicker
                                key={`end-${isModalOpen}-${tempEndDate?.getTime()}`}
                                selected={tempEndDate}
                                onChange={handleEndDateChange}
                                minDate={tempStartDate || minDate}
                                maxDate={maxDate}
                                dateFormat="MMMM d, yyyy"
                                calendarClassName="date-range-calendar date-range-calendar-end"
                                inline
                                locale="en-GB"
                                formatWeekDay={dayName => {
                                    const shortDay = dayName.substring(0, 3)
                                    return shortDay.charAt(0).toUpperCase() + shortDay.slice(1).toLowerCase()
                                }}
                            />
                        </div>
                    </div>
                    <div className="text-center mt-3">
                        <button
                            className="btn btn-primary"
                            onClick={handleConfirm}
                            disabled={!tempStartDate || !tempEndDate}
                            style={{ minWidth: '120px' }}
                        >
                            Confirm
                        </button>
                    </div>
                </ModalBody>
            </Modal>
        </>
    )
}

export default DateRangePicker
